/**
 * Cihaz adı, göz sayısı, değirmen ve su modu vb. için ortak yardımcılar.
 */

export function getDeviceDisplayName(device) {
  const name = device?.deviceInfo?.deviceName ?? device?.deviceName ?? "";
  return String(name).trim() || "Bilinmeyen Cihaz";
}

export function getDeviceCupNumber(device) {
  const cup = device?.deviceInfo?.cupNumber ?? device?.cupNumber;
  return cup != null ? Number(cup) : 0;
}

/**
 * Cihazda değirmen bileşeni var mı? (API'den hasGrinder veya deviceComponents ile)
 */
export function deviceHasGrinder(device, hasGrinderFromApi) {
  if (hasGrinderFromApi === true || hasGrinderFromApi === false) {
    return hasGrinderFromApi;
  }
  const components = device?.deviceComponents?.components ?? [];
  // Deaktif (active:false, fault:false) grinder "değirmensiz" sayılır;
  // sadece aktif veya arızalı grinder "var" kabul edilir (kullanıcı kararı).
  return (
    Array.isArray(components) &&
    components.some((c) => c?.key === "grinder" && (c?.active || c?.fault))
  );
}

export function formatCupLabel(cupNumber) {
  if (typeof cupNumber === "number") return `${cupNumber} göz`;
  if (cupNumber != null && cupNumber !== "") return `${cupNumber} göz`;
  return "—";
}

export function formatGrinderDisplay(hasGrinder) {
  return hasGrinder ? "Değirmenli" : "Değirmensiz";
}

/**
 * Cihaz tipi adında "Fincanda" geçiyorsa değirmen etiketi gösterilir.
 */
export function shouldShowGrinderForDevice(deviceNameOrLabel) {
  return /fincanda/i.test(String(deviceNameOrLabel || ""));
}

/**
 * Etiketten göz sayısını çıkarır (örn. "2'li Çayset" -> 2).
 */
export function getCupFromDeviceLabel(label) {
  const m = String(label || "").match(/^(\d+)'li/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Cihaz bileşenlerinden key'e göre filtreler.
 */
export function getDeviceComponentsByKey(device, key) {
  const components = device?.deviceComponents?.components ?? [];
  if (!Array.isArray(components) || !key) return [];
  return components.filter((c) => c?.key === key);
}

/**
 * Cihazdan grinder durumunu döndürür: { label: "Aktif" | "Arızalı" | "-" }
 * Deaktif (Pasif) grinder "-" döner — değirmensiz sayılır.
 */
export function getGrinderStatus(device) {
  const components = getDeviceComponentsByKey(device, "grinder");
  if (!components.length) return { label: "-" };
  if (components.some((c) => c?.fault)) return { label: "Arızalı" };
  if (components.some((c) => c?.active)) return { label: "Aktif" };
  // Pasif (component var ama aktif değil, arızalı değil) → "-": deaktif
  // değirmen "değirmensiz" sayılır, tüm `!== "-"` korumaları onu gizler.
  return { label: "-" };
}

/** Su modu (şebeke/damacana) bu cihaz tipi için geçerli mi? */
export function isWaterModeApplicable(deviceNameOrLabel) {
  const name = String(deviceNameOrLabel || "").toLowerCase();
  if (name.includes("espresso")) return false;
  return /fincanda|çayset|çay-filtre/i.test(name);
}

// Bayi tarafındaki konumlar — cihaz müşteride değil, bayinin elindedir.
// "Firma" bunun dışındadır: cihaz bayinin müşteri firmasında sahada çalışır.
const DEALER_HELD_STATUSES = ["Depo", "Servis", "Plasiyer"];

/**
 * Cihaz bayi panelinde bayinin elinde mi duruyor? (depo / servis / plasiyer)
 *
 * İki sinyalden biri yeterlidir: cihazın firması giriş yapan bayinin firması,
 * ya da bayi konumu ("dealerDeviceStatusName") müşteri firması dışındaki bir
 * değer. İkincisi alt bayi firmalarındaki cihazları da kapsar.
 *
 * @param auth          useAuth().auth — rol ve firma id'si buradan okunur
 * @param device        liste/detay cihaz objesi (firm populate olmayabilir)
 * @param fallbackFirmId  cihazda firm yoksa bağlam firması (ör. firma sekmesi)
 */
export function isDeviceHeldByDealer(auth, device, fallbackFirmId = null) {
  if (auth?.role !== "dealer") return false;
  if (DEALER_HELD_STATUSES.includes(device?.dealerDeviceStatusName)) return true;
  const dealerFirmId = auth?.firm ? String(auth.firm) : null;
  if (!dealerFirmId) return false;
  const deviceFirmId = device?.firm?._id ?? device?.firm ?? fallbackFirmId;
  return deviceFirmId ? String(deviceFirmId) === dealerFirmId : false;
}

/**
 * Bayi paneli cihaz taşıma aksiyonları (#247, sözleşme dealer-device-moves.md).
 * İzin kontrolü çağıranda kalır; burada yalnız cihazın konumuna göre kural:
 *
 * - toSubFirm   "Alt Firmaya Ver"  — her zaman (devices.moveToFirm)
 * - toSubDealer "Alt Bayiye Ver"   — bayinin alt bayisi varsa (devices.moveToFirm)
 * - takeBack    "Bayiye Geri Al"   — cihaz başka firmadaysa (devices.moveToStorage)
 * - toStorage   "Depoya Al"        — kendi firmasında plasiyer/serviste (devices.moveToStorage)
 * - toService   "Servise Al"       — her zaman; merkez servisine gider (devices.moveToService)
 *
 * @returns {string[]} aksiyon anahtarları; bayi değilse boş
 */
export function getDealerMoveActions(auth, device, { hasSubDealers = false, fallbackFirmId = null } = {}) {
  if (auth?.role !== "dealer") return [];
  const dealerFirmId = auth?.firm ? String(auth.firm) : null;
  const deviceFirmId = device?.firm?._id ?? device?.firm ?? fallbackFirmId;
  const inOwnFirm = !!dealerFirmId && !!deviceFirmId && String(deviceFirmId) === dealerFirmId;
  const actions = ["toSubFirm"];
  if (hasSubDealers) actions.push("toSubDealer");
  if (!inOwnFirm) actions.push("takeBack");
  else if (["Plasiyer", "Servis"].includes(device?.dealerDeviceStatusName)) actions.push("toStorage");
  actions.push("toService");
  return actions;
}

/**
 * Merkez panelinde bayi adına taşıma (#329, sözleşme dealer-device-moves.md v1.1).
 * İzin kontrolü çağıranda kalır; burada yalnız cihazın firmasına göre kural:
 *
 * - toCustomer "Bayi Müşterisine Ver" — cihazın firması bayi (devices.moveToFirm)
 * - takeBack   "Bayiye Geri Al"       — cihazın firması bayiye bağlı müşteri (devices.moveToStorage)
 *
 * `device.firm` dolu (populate) olmalı; kimlik olarak gelirse aksiyon yok.
 * @returns {string[]} aksiyon anahtarları; bayi panelinde boş
 */
export function getCenterDealerMoveActions(auth, device) {
  if (auth?.role === "dealer") return [];
  const firm = device?.firm;
  if (!firm || typeof firm !== "object") return [];
  if (firm.firmType === "dealer") return ["toCustomer"];
  if (firm.firmType === "customer" && firm.dealerFirm?.length > 0) return ["takeBack"];
  return [];
}

/**
 * Bayinin alt bayisi var mı? `firms` = /api/firms/dealerRoles/getFirms listesi
 * (bayinin kendisi + alt bayiler + müşteriler).
 */
export function dealerHasSubDealers(auth, firms = []) {
  const dealerFirmId = auth?.firm ? String(auth.firm) : null;
  return firms.some((f) => f?.firmType === "dealer" && String(f._id) !== dealerFirmId);
}

/**
 * Açık/Kapalı (isActive) rozeti gösterilsin mi?
 * Bayinin elindeki cihaz sahada değildir ve sunucuda her taşımada kapalı
 * alınır (bkz. device-status-dealer.controller) — durum sabit olduğu için
 * rozet hiç basılmaz. Bağlı/Bağlı Değil rozeti bundan etkilenmez.
 * Merkezde (#255): bayideki (firması bayi) kapalı cihazda da basılmaz; açıksa
 * (#333 öncesi yanlışlıkla açılmış) "Kota Almaya Açık" görünür.
 */
export function shouldShowDevicePowerState(auth, device, fallbackFirmId = null) {
  if (auth?.role !== "dealer") {
    return !(device?.firm?.firmType === "dealer" && !device?.isActive);
  }
  return !isDeviceHeldByDealer(auth, device, fallbackFirmId);
}

/**
 * "Kota Almaya Aç/Kapa" (isActive) aksiyonu gösterilsin mi?
 * Bayinin elindeki cihazda gizlenir — sunucu da bu uçta 400 döner.
 * Merkezde (#333): bayideki (firması bayi) kapalı cihaz açılamaz; yanlışlıkla
 * açık kalmışsa kapatılabilsin diye "Kapa" görünür.
 */
export function canToggleDeviceActivity(auth, device, fallbackFirmId = null) {
  if (auth?.role !== "dealer") {
    return !(device?.firm?.firmType === "dealer" && !device?.isActive);
  }
  return !isDeviceHeldByDealer(auth, device, fallbackFirmId);
}

export const WATER_MODE_COMPONENT_KEYS = {
  mains: "waterTankMainsWaterPump",
  carboy: "waterTankCarboyWaterPump",
};

/**
 * Cihazın su modu durumu — yalnızca AKTİF pompalardan türetilir; deaktif
 * (pasif) pompa "yok" sayılır (değirmen kuralıyla aynı). Server
 * componentSummary'sine bağlı kalmadan, gerçek component verisinden hesaplar
 * (her yerde tutarlı, restart bağımsız).
 * @returns {{label: "Şebeke"|"Damacana"|"Şebeke + Damacana"|"Arızalı"|"-"}}
 */
export function getWaterModeStatus(device) {
  const name = device?.deviceInfo?.deviceName || device?.deviceName || "";
  if (!isWaterModeApplicable(name)) return { label: "-" };
  const mains = getDeviceComponentsByKey(device, WATER_MODE_COMPONENT_KEYS.mains);
  const carboy = getDeviceComponentsByKey(device, WATER_MODE_COMPONENT_KEYS.carboy);
  const mainsActive = mains.some((c) => c?.active);
  const carboyActive = carboy.some((c) => c?.active);
  const anyFault = mains.some((c) => c?.fault) || carboy.some((c) => c?.fault);
  if (mainsActive && carboyActive) return { label: "Şebeke + Damacana" };
  if (mainsActive) return { label: "Şebeke" };
  if (carboyActive) return { label: "Damacana" };
  if (anyFault) return { label: "Arızalı" };
  return { label: "-" };
}
